import React from "react";
import { Link } from "react-router-dom";
import { Clock, Users, Tag, ShieldCheck, MapPin } from "lucide-react";
import { formatRupiah } from "./ui";
import { StarRating } from "./StarRating";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function workerCoverSrc(worker) {
  if (!worker?.cover_photo_url) return null;
  if (/^https?:\/\//.test(worker.cover_photo_url)) return worker.cover_photo_url;
  return `${BACKEND_URL}${worker.cover_photo_url}`;
}

function workerProfileSrc(worker) {
  if (!worker?.profile_photo_url) return null;
  if (/^https?:\/\//.test(worker.profile_photo_url)) return worker.profile_photo_url;
  return `${BACKEND_URL}${worker.profile_photo_url}`;
}

const ICON_MAP = {
  broom: "🧹",
  snowflake: "❄️",
  droplets: "💧",
  zap: "⚡",
  paintbrush: "🎨",
  truck: "🚛",
  trees: "🌳",
  cpu: "🔧",
  massage: "💆",
};

export function CategoryCard({ category, to }) {
  return (
    <Link
      to={to}
      className="card p-4 text-left hover:border-brand-200 transition group"
      data-testid={`category-card-${category.slug}`}
    >
      <div className="h-11 w-11 rounded-xl bg-brand-50 grid place-items-center text-xl">
        {ICON_MAP[category.icon] || "📋"}
      </div>
      <p className="font-bold text-brand-800 mt-3">{category.name}</p>
      <p className="text-sm text-brand-400 mt-0.5 line-clamp-2">{category.description}</p>
    </Link>
  );
}

export function ServiceCard({ service, to }) {
  const pricingLabel = {
    FIXED: "Harga Tetap",
    STARTING_FROM: "Mulai dari",
    QUOTATION: "Negosiasi",
  };

  return (
    <Link
      to={to}
      className="card p-4 hover:border-brand-200 transition block"
      data-testid={`service-card-${service.slug}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-bold text-brand-800 truncate">{service.name}</p>
          {service.category && (
            <span className="chip bg-brand-50 text-brand-600 text-xs mt-1 inline-flex">
              {service.category.name}
            </span>
          )}
          <p className="text-sm text-brand-500 mt-2 line-clamp-2">
            {service.description || "Tidak ada deskripsi"}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-lg font-extrabold text-brand-800">
            {formatRupiah(service.base_price)}
          </p>
          <p className="text-xs text-brand-400">
            {pricingLabel[service.pricing_type] || ""}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-brand-50 text-sm text-brand-500">
        {service.duration_estimate && (
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> {service.duration_estimate}
          </span>
        )}
        {service.worker_count !== undefined && (
          <span className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" /> {service.worker_count} pekerja
          </span>
        )}
      </div>
    </Link>
  );
}

export function WorkerCard({ worker, serviceSlug }) {
  const cover = workerCoverSrc(worker);
  const avatar = workerProfileSrc(worker);
  const pic = avatar || cover;
  return (
    <Link
      to={`/customer/workers/${worker.worker_id}?service=${serviceSlug || ""}`}
      className="card p-4 hover:border-brand-200 transition flex items-center gap-3"
      data-testid={`worker-card-${worker.worker_id}`}
    >
      {pic ? (
        <img
          src={pic}
          alt={worker.display_name || "Foto pekerja"}
          className="h-12 w-12 rounded-full object-cover flex-shrink-0 border border-brand-100"
          loading="lazy"
        />
      ) : (
        <div className="h-12 w-12 rounded-full bg-brand-100 grid place-items-center text-lg font-bold text-brand-700 flex-shrink-0">
          {(worker.display_name || "W")[0].toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-bold text-brand-800">{worker.display_name || "Pekerja"}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="chip bg-brand-50 text-brand-600 text-xs">{worker.tier}</span>
          {worker.city && (
            <span className="flex items-center gap-1 text-xs text-brand-400">
              <MapPin className="h-3 w-3" /> {worker.city}
              {worker.city_match && (
                <span className="chip bg-success-50 text-success-600 text-[10px] font-bold">Terdekat</span>
              )}
            </span>
          )}
          {worker.kyc_status === "APPROVED" && (
            <span className="flex items-center gap-1 text-xs text-success-600">
              <ShieldCheck className="h-3 w-3" /> Terverifikasi
            </span>
          )}
          {worker.average_rating && (
            <StarRating rating={worker.average_rating} count={worker.review_count} />
          )}
          {worker.custom_price != null && (
            <span className="flex items-center gap-1 text-sm text-brand-500">
              <Tag className="h-3 w-3" /> {formatRupiah(worker.custom_price)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
